import React, { useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Typography from "../../../../components/Typography/Typography";
import Stepper from "../../../../components/sct/Stepper/Stepper";
import SelectAssetType from "./SelectAssetType";
import UploadDocument from "./UploadDocument";
import {
    useGetAssetsTypeQuery,
    useCreateAssetMutation,
} from "../../../../services/supplyChainService";
import {
    useUploadDocumentMutation,
    useProcessDocumentMutation,
} from "../../../../services/aiAssistService";
import Loading from "../../../../components/LoadingSkeleton/Loading";
import ReviewEditAsset from "./ReviewEditAsset";
import SubmitAsset from "./SubmitAsset";
import { toast } from "react-toastify";
import Button from "../../../../components/Button/Button";
import { useModal } from "../../../../hooks/modalsHooks";

// Utility functions
const handleUsageLimitError = (error) => {
    if (
        error?.data?.errors?.[0]?.message?.includes(
            "exceeded the document mapper usage limit",
        )
    ) {
        const retryAfter = error.data.errors[0].retry_after;
        const retryDate = new Date(retryAfter);
        const formattedDate = retryDate.toLocaleDateString();
        const formattedTime = retryDate.toLocaleTimeString();

        toast.error(
            `You have exceeded the document processing limit. Please try again after ${formattedDate} at ${formattedTime}.`,
            { autoClose: 10000 },
        );
        return true;
    }
    return false;
};

const processDocuments = async (
    documentsToProcess,
    batchId,
    selectedAssetType,
    processDocument,
) => {
    const processResponse = await processDocument({
        batch: batchId,
        asset_type_id: selectedAssetType?.id,
        documents_to_process: documentsToProcess,
    }).unwrap();

    if (processResponse.data && processResponse.data.extracted_assets) {
        return processResponse.data.extracted_assets.map((asset) => ({
            ...asset,
            asset_type_id: selectedAssetType?.id || null,
        }));
    }
    return [];
};

const processFormData = (formData) => {
    return (
        formData?.map((field) => {
            if (field.type === "file" || field.type === "image") {
                return {
                    ...field,
                    value:
                        typeof field.value === "object" && field.value.id
                            ? field.value.id
                            : field.value,
                };
            }

            if (field.type === "group" && Array.isArray(field.value)) {
                return {
                    ...field,
                    value: field.value.map((groupSet) =>
                        groupSet.map((groupField) => {
                            if (
                                groupField.type === "file" ||
                                groupField.type === "image"
                            ) {
                                return {
                                    ...groupField,
                                    value:
                                        typeof groupField.value === "object" &&
                                        groupField.value.id
                                            ? groupField.value.id
                                            : groupField.value,
                                };
                            }
                            return groupField;
                        }),
                    ),
                };
            }

            return field;
        }) || []
    );
};

const createAssetRequest = (asset, selectedAssetType, id, batchId) => {
    const imageIds =
        asset.asset_images
            ?.map((img) => (typeof img === "object" && img.id ? img.id : img))
            .filter(Boolean) || [];

    const processedFormData = processFormData(asset.form_data);

    return {
        id: id,
        asset_id: asset.asset_id || asset.assetId,
        asset_type: asset.asset_type_id || selectedAssetType?.id,
        qty: asset?.qty || 0,
        unit: typeof asset.unit === "object" ? asset.unit.id : asset.unit,
        form_data: processedFormData,
        dependencies: asset.dependencies || [],
        asset_images: imageIds,
        batch_id: batchId || null,
    };
};

function AIAssistPage({ onBack, batchId }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const uploadDocumentRef = useRef(null);
    const { showModal } = useModal();

    // Core state - keeping only essential state
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedAssetType, setSelectedAssetType] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [documentIdMap, setDocumentIdMap] = useState(new Map());
    const [extractedData, setExtractedData] = useState([]);
    const [originalExtractedCount, setOriginalExtractedCount] = useState(0);
    const [submissionResults, setSubmissionResults] = useState(null);

    // Processing state - simplified to essential flags
    const [isExtracting, setIsExtracting] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasDependencyWarnings, setHasDependencyWarnings] = useState(false);

    // API hooks
    const [uploadDocument] = useUploadDocumentMutation();
    const [processDocument] = useProcessDocumentMutation();
    const [createAsset] = useCreateAssetMutation();

    const { data: assetTypesResponse, isLoading: isLoadingAssetTypes } =
        useGetAssetsTypeQuery(id);

    // Memoized computed values
    const assetTypeList = useMemo(() => {
        return assetTypesResponse?.data?.map((assetType) => ({
            id: assetType.id,
            label: assetType.type_name,
            value: assetType.id,
            dependencyConfig: assetType.dependency_config,
            formConfig: assetType.form_config,
            unit_category: assetType.unit_category,
            location: assetType.node,
            material_type: assetType.material_type,
        }));
    }, [assetTypesResponse]);

    const isProcessing = useMemo(
        () => isExtracting || isSubmitting,
        [isExtracting, isSubmitting],
    );
    const disableNavigation = useMemo(
        () => isProcessing || isRetrying,
        [isProcessing, isRetrying],
    );

    // Event handlers
    const handleOnAssetTypeSelectionChange = (event) => {
        const selection = event.target.value;
        if (!selection || selection === "") {
            setSelectedAssetType(null);
            return;
        }

        const assetType = assetTypeList?.find((at) => at.value === selection);
        setSelectedAssetType(assetType || null);
    };

    const getOrUploadDocument = async (file) => {
        const fileKey = `${file.name}-${file.size}-${file.lastModified || 0}`;

        if (documentIdMap.has(fileKey)) {
            return documentIdMap.get(fileKey);
        }

        if (file instanceof File) {
            const formData = new FormData();
            const extension = file.type.split("/")[1] || "";

            formData.append("extension", extension);
            formData.append("document_file", file);
            formData.append("name", file.name);
            formData.append("asset_type", selectedAssetType?.id);

            const uploadResponse = await uploadDocument({
                formData: formData,
            }).unwrap();

            const uploadedDoc = uploadResponse.data || file;
            setDocumentIdMap((prev) => new Map(prev.set(fileKey, uploadedDoc)));
            return uploadedDoc;
        }

        return file;
    };

    const uploadFilesOptimized = async (documents) => {
        const uploadPromises = documents.map(getOrUploadDocument);
        return Promise.all(uploadPromises);
    };

    const handleFilesSelected = (files) => {
        setSelectedFiles(files);

        if (files.length === 0) {
            setDocumentIdMap(new Map());
        } else {
            const currentFileKeys = new Set(
                files.map(
                    (file) =>
                        `${file.name}-${file.size}-${file.lastModified || 0}`,
                ),
            );

            setDocumentIdMap((prev) => {
                const newMap = new Map();
                for (const [key, value] of prev.entries()) {
                    if (currentFileKeys.has(key)) {
                        newMap.set(key, value);
                    }
                }
                return newMap;
            });
        }
    };

    const handleAssetUpdated = (updatedAsset) => {
        setExtractedData((prevData) => {
            return prevData.map((asset) => {
                const assetIdToCompare = asset.asset_id || asset.assetId;
                const updatedAssetIdToCompare =
                    updatedAsset.asset_id || updatedAsset.assetId;
                return assetIdToCompare === updatedAssetIdToCompare
                    ? updatedAsset
                    : asset;
            });
        });
    };

    const handleAssetDeleted = (assetIdToDelete) => {
        setExtractedData((prevData) => {
            return prevData.filter((asset) => {
                const assetId = asset.asset_id || asset.assetId;
                return assetId !== assetIdToDelete;
            });
        });
    };

    const handleSubmitAssets = async () => {
        if (extractedData.length === 0) {
            toast.error("No assets to submit");
            return;
        }

        setSubmissionResults(null);
        setIsSubmitting(true);

        try {
            const results = [];
            let successCount = 0;
            let errorCount = 0;
            let errorMessages = [];

            for (const asset of extractedData) {
                try {
                    const requestData = createAssetRequest(
                        asset,
                        selectedAssetType,
                        id,
                        batchId,
                    );
                    const response = await createAsset(requestData);

                    if (response.error) {
                        const errorData = response.error.data;
                        if (
                            errorData?.errors &&
                            Array.isArray(errorData.errors)
                        ) {
                            const assetErrors = errorData.errors.filter(
                                (error) =>
                                    error.includes(
                                        asset.asset_id || asset.assetId,
                                    ),
                            );
                            throw new Error(
                                assetErrors.length > 0
                                    ? assetErrors.join("; ")
                                    : errorData.message ||
                                      "Unknown error occurred",
                            );
                        } else {
                            throw new Error(
                                errorData?.message || "Unknown error occurred",
                            );
                        }
                    }

                    results.push({
                        asset: asset.asset_id || asset.assetId,
                        success: true,
                    });
                    successCount++;
                } catch (error) {
                    const errorMessage = error.message;
                    errorMessages.push(
                        `${asset.asset_id || asset.assetId}: ${errorMessage}`,
                    );

                    results.push({
                        asset: asset.asset_id || asset.assetId,
                        success: false,
                        error: errorMessage,
                    });
                    errorCount++;
                }
            }

            // Handle submission results and update state accordingly
            if (successCount > 0 && errorCount === 0) {
                toast.success(
                    `Successfully created all ${successCount} assets!`,
                );
                setExtractedData([]);
            } else if (successCount > 0 && errorCount > 0) {
                toast.success(`Successfully created ${successCount} assets!`);
                toast.error(
                    `Failed to create ${errorCount} assets. ${errorMessages.join(" ")}`,
                    { autoClose: 8000 },
                );

                const failedAssets = extractedData.filter((asset) => {
                    const assetId = asset.asset_id || asset.assetId;
                    return results.find(
                        (r) => r.asset === assetId && !r.success,
                    );
                });
                setExtractedData(failedAssets);
            } else if (errorCount > 0) {
                toast.error(
                    `Failed to create all ${errorCount} assets. ${errorMessages.join(" ")}`,
                    { autoClose: 8000 },
                );
            }

            setSubmissionResults({
                successCount,
                errorCount,
                errorMessages,
                results,
            });
        } catch (error) {
            toast.error("Failed to submit assets: " + error.message);
            setSubmissionResults({
                successCount: 0,
                errorCount: extractedData.length,
                errorMessages: [error.message],
                results: [],
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleExtractionStart = async (documents) => {
        setIsExtracting(true);

        try {
            const uploadedFileData = await uploadFilesOptimized(documents);
            const documentsToProcess = uploadedFileData.map((file) => file.id);
            const processedExtractedData = await processDocuments(
                documentsToProcess,
                batchId,
                selectedAssetType,
                processDocument,
            );

            setExtractedData(processedExtractedData);
            setOriginalExtractedCount(processedExtractedData.length);
            setCurrentStep(3); // Move to review step
        } catch (error) {
            if (!handleUsageLimitError(error)) {
                toast.error(
                    "Error during document upload or AI extraction. Please try again.",
                );
            }
        } finally {
            setIsExtracting(false);
        }
    };

    const handleRetryExtraction = async () => {
        const currentFiles =
            uploadDocumentRef.current?.getCurrentFiles() || selectedFiles;

        if (!selectedAssetType || currentFiles.length === 0) {
            toast.error("No uploaded documents to reprocess.");
            return;
        }

        setIsRetrying(true);

        try {
            const uploadedFileData = await uploadFilesOptimized(currentFiles);
            const documentsToProcess = uploadedFileData.map((file) => file.id);
            const processedExtractedData = await processDocuments(
                documentsToProcess,
                batchId,
                selectedAssetType,
                processDocument,
            );

            setExtractedData(processedExtractedData);
            setOriginalExtractedCount(processedExtractedData.length);
            toast.success("Document reprocessed successfully");
        } catch (error) {
            if (!handleUsageLimitError(error)) {
                toast.error("Failed to reprocess documents. Please try again.");
            }
        } finally {
            setIsRetrying(false);
        }
    };

    // Step configuration
    const steps = [
        {
            title: "Select Asset Type",
            subtitle: "Choose the type of assets to extract",
            content: (
                <SelectAssetType
                    selectedAssetType={selectedAssetType}
                    assetTypeList={assetTypeList}
                    onAssetTypeChange={handleOnAssetTypeSelectionChange}
                />
            ),
        },
        {
            title: "Upload Documents",
            subtitle: "Upload up to 5 documents for AI processing",
            content: (
                <UploadDocument
                    ref={uploadDocumentRef}
                    selectedAssetType={selectedAssetType}
                    onFilesSelected={handleFilesSelected}
                    isExtracting={isExtracting}
                    selectedFiles={selectedFiles}
                />
            ),
        },
        {
            title: "Review & Edit",
            subtitle: "Review and edit extracted asset data",
            content: (
                <ReviewEditAsset
                    extractedData={extractedData}
                    selectedAssetType={selectedAssetType}
                    assetTypeList={assetTypeList}
                    onAssetUpdated={handleAssetUpdated}
                    onAssetDeleted={handleAssetDeleted}
                    originalExtractedCount={originalExtractedCount}
                    onRetry={handleRetryExtraction}
                    isRetrying={isRetrying}
                    isSubmitting={isSubmitting}
                    onDependencyWarning={setHasDependencyWarnings}
                />
            ),
        },
        {
            title: "Submit Assets",
            subtitle: submissionResults
                ? submissionResults.errorCount === 0
                    ? "All assets submitted successfully"
                    : submissionResults.successCount > 0
                      ? "Some assets submitted successfully"
                      : "Asset submission failed"
                : "Assets have been submitted successfully",
            content: (
                <SubmitAsset
                    submissionResults={submissionResults}
                    extractedCount={extractedData.length}
                    onBack={onBack}
                    onRetry={() => {
                        setSubmissionResults(null);
                        setCurrentStep(3);
                    }}
                    isSubmitting={isSubmitting}
                />
            ),
        },
    ];

    // Navigation handlers
    const handleNext = async () => {
        if (currentStep === 1) {
            if (!selectedAssetType) {
                toast.error("Please select an asset type before proceeding.");
                return;
            }
            setCurrentStep(2);
            return;
        }

        if (currentStep === 2) {
            const isValid = uploadDocumentRef.current?.triggerValidation();
            if (!isValid) return;

            const currentFiles =
                uploadDocumentRef.current?.getCurrentFiles() || selectedFiles;
            if (currentFiles.length === 0) {
                toast.info(
                    "Please upload at least one document before starting extraction.",
                );
                return;
            }

            await handleExtractionStart(currentFiles);
            return;
        }

        if (currentStep === 3) {
            if (extractedData.length === 0) {
                toast.error(
                    "No assets to submit. Please extract assets first.",
                );
                return;
            }

            // Check for dependency warnings and prevent submission
            if (hasDependencyWarnings) {
                toast.error(
                    "Cannot proceed. Some assets have missing dependencies. Please configure dependencies for all assets before submitting.",
                    { autoClose: 8000 },
                );
                return;
            }

            const confirmed = await new Promise((resolve) => {
                showModal({
                    modalType: "ConfirmAssetModal",
                    modalProps: {
                        assetCount: extractedData.length,
                        onConfirm: () => {
                            setCurrentStep(4);
                            handleSubmitAssets();
                            resolve(true);
                        },
                        onCancel: () => resolve(false),
                        isSubmitting: isSubmitting,
                    },
                });
            });

            if (!confirmed) return;
            return;
        }

        if (currentStep < steps.length) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleComplete = () => {
        navigate("/sct/data-entry");
    };

    const getNextButtonText = () => {
        switch (currentStep) {
            case 1:
                return "Next";
            case 2:
                return isExtracting
                    ? "Processing with AI..."
                    : "Start Extraction";
            case 3:
                return hasDependencyWarnings
                    ? "Configure Dependencies"
                    : "Submit Assets";
            case 4:
                return isSubmitting ? "Creating Assets..." : "Complete";
            default:
                return "Next";
        }
    };

    const getNextButtonDisabled = () => {
        switch (currentStep) {
            case 1:
                return !selectedAssetType || !selectedAssetType.id;
            case 2:
                return isExtracting || selectedFiles.length === 0;
            case 3:
                return (
                    isRetrying ||
                    extractedData.length === 0 ||
                    hasDependencyWarnings
                );
            case 4:
                return isSubmitting;
            default:
                return false;
        }
    };

    if (isLoadingAssetTypes) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "100vh" }}
            >
                <Loading />
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12 pt-2">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                            <Typography variant="h2" className="mb-2 text-2xl">
                                Add assets
                            </Typography>
                            <Typography variant="body1" className="mb-4">
                                Use AI to extract asset data from your documents
                            </Typography>
                        </div>
                        <Button
                            onClick={onBack}
                            variant="secondary"
                            fullWidth={false}
                        >
                            Back to Data Entry
                        </Button>
                    </div>
                </div>
            </div>

            <Stepper
                steps={steps}
                currentStep={currentStep}
                onNext={handleNext}
                onPrevious={handlePrevious}
                onComplete={handleComplete}
                showProgress={true}
                showNavigation={true}
                className="mt-4"
                nextButtonText={getNextButtonText()}
                isProcessing={isProcessing}
                disablePrevious={disableNavigation}
                disableNext={getNextButtonDisabled()}
            />
        </div>
    );
}

export default AIAssistPage;
